/**
 * Bridge: project the docx-native editor's LIVE tracked changes into the
 * relational `document_suggestions` table.
 *
 * WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * The entire PropDocs backend counts/gates suggestions off `document_suggestions`
 * rows, NOT off the document body:
 *   - deal "send for signing" is blocked while `document_suggestions.pending`
 *     exists (prepare_to_sign_validator_service / Document#change_suggestions_complete?)
 *   - template publish is blocked while pending suggestions exist
 *     (DocumentTemplate#has_pending_suggestions?)
 *   - notification badges count these rows.
 * The CKEditor track-changes adapter kept that table in sync. The docx-native
 * editor stores tracked changes only INSIDE the .docx body (`w:ins`/`w:del`),
 * so without this bridge the table stays empty and every one of those gates is
 * blind — you can publish / send with unresolved suggestions, and counts read 0.
 *
 * WHAT IT DOES ────────────────────────────────────────────────────────────
 * On each editor change (debounced, alongside autosave) it:
 *   1. takes the doc's current tracked changes (body AND header/footer),
 *   2. maps each to a STABLE `external_id` derived from its `revisionId`,
 *   3. `createDocumentSuggestion` for ids not seen before,
 *   4. `syncDocumentSuggestions` with the full current id set — the backend
 *      marks any pending row whose id is no longer present as `deleted`
 *      (a change that was accepted/rejected in-editor disappears from the body).
 * The relational table therefore MIRRORS the live document's pending changes.
 *
 * external_id SHAPE: `docx:{documentType}:{documentId}:{revisionId}`.
 * `revisionId` (the OOXML `w:id`) is only unique WITHIN one document, but the
 * table's `external_id` is globally unique — so it must be namespaced by
 * document. The shape is deterministic, so re-deriving it on every sync gives
 * the SAME id for the same change (no duplicate rows across sessions).
 *
 * IDEMPOTENT + FAIL-SOFT: a create is only issued for an id the server has
 * no row for. The hook learns which rows exist from the reconcile response, so
 * it holds without the caller seeding anything — the template editor never
 * did, and every reopened template re-created its pending suggestions and
 * logged "External has already been taken". Syncs run one at a time: two
 * overlapping calls (the debounced autosave and a deal-flow flush) would
 * otherwise both see a new id as unknown and both create it. A failed sync
 * never blocks editing or autosave — the gate keeps the last known state.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client';
import type { TrackedChangeEntry } from '@propdocs-editor/core/utils/comments';
import { mutations } from '../../../../api';
import { decodeAuthorToken } from './authorToken';
import {
  entryKind,
  externalIdMap,
  type SuggestionSyncDocumentType,
} from './docxSuggestionSync';

export type { SuggestionSyncDocumentType } from './docxSuggestionSync';

export interface DocxSuggestionSyncHandle {
  /**
   * Reconcile the relational table against the editor's current tracked
   * changes. Resolves with the number of pending suggestions now in the doc
   * (the count the send/publish gates and notification badges care about).
   *
   * Takes the ENTRY LIST rather than an `EditorState`. It used to take the body
   * view's state and call `extractTrackedChanges` itself — but header and footer
   * content live in their own ProseMirror views, so a change made in a header
   * was invisible here: it never created a row, and the reconcile step then
   * marked any row it DID have as deleted. The signing gate counted zero and let
   * a document with unresolved header changes through. `DocxEditorRef
   * .getTrackedChanges()` returns the merged body + H/F list the review sidebar
   * already renders.
   */
  sync: (entries: readonly TrackedChangeEntry[] | null) => Promise<number>;
}

/**
 * Build the sync handle for a deal document or a template. Returns a stable
 * `sync(state)` the container calls (debounced) on editor change.
 */
export function useDocxSuggestionSync(
  documentId: string | undefined,
  documentType: SuggestionSyncDocumentType,
  /**
   * external_ids that ALREADY exist in `document_suggestions` for this record
   * (from `getDocumentById.documentSuggestions`). Optional: the hook reads the
   * existing rows from the server before its first create anyway; a seed that
   * already covers every live id just saves that round trip.
   */
  existingExternalIds?: readonly string[]
): DocxSuggestionSyncHandle {
  const [createDocumentSuggestion] = useMutation(mutations.createDocumentSuggestion);
  const [syncDocumentSuggestions] = useMutation(mutations.syncDocumentSuggestions);

  // external_ids the server has a row for, in ANY status. A row is never
  // removed — rejecting or accepting a change only reconciles it to `deleted` —
  // so once an id is here a create for it can only fail, even if the change
  // leaves the document and comes back (undo).
  const knownRef = useRef<Set<string>>(new Set());
  // Records whose existing rows have been read from the server this session.
  const primedRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    if (existingExternalIds) {
      for (const id of existingExternalIds) knownRef.current.add(id);
    }
  }, [existingExternalIds]);

  const reconcile = useCallback(
    async (docId: string, externalIds: string[]): Promise<boolean> => {
      try {
        const { data } = await syncDocumentSuggestions({
          variables: { documentId: docId, documentType, externalIds },
        });
        const rows = (data?.syncDocumentSuggestions ?? []) as Array<{ externalId?: string | null }>;
        for (const row of rows) if (row?.externalId) knownRef.current.add(String(row.externalId));
        return true;
      } catch {
        // Keep the last known state on failure; the next change retries.
        return false;
      }
    },
    [documentType, syncDocumentSuggestions]
  );

  const runSync = useCallback(
    async (entries: readonly TrackedChangeEntry[] | null): Promise<number> => {
      if (!documentId) return 0;
      const changes = entries ?? [];

      // Build the id → representative-entry map for THIS document's live changes.
      const byExternalId = externalIdMap(changes, documentType, documentId);
      const currentIds = [...byExternalId.keys()];

      // 1. Learn which rows already exist before the first create of the session.
      //    Reconciling here is the same call step 3 makes, so it is safe to run
      //    with the live id set.
      const primeKey = `${documentType}:${documentId}`;
      if (!primedRef.current.has(primeKey) && currentIds.some((id) => !knownRef.current.has(id))) {
        if (await reconcile(documentId, currentIds)) primedRef.current.add(primeKey);
      }

      // 2. Create rows for ids the server has never seen.
      const toCreate = currentIds.filter((id) => !knownRef.current.has(id));
      await Promise.all(
        toCreate.map(async (externalId) => {
          const entry = byExternalId.get(externalId)!;
          const token = decodeAuthorToken(entry.author);
          try {
            await createDocumentSuggestion({
              variables: {
                documentId,
                documentType,
                externalId,
                type: entryKind(entry.type),
                // Enough to reconstruct the change for display/threading; the
                // body remains the source of truth for the actual content.
                data: {
                  authorId: token.authorId ?? null,
                  authorName: token.display,
                  text: entry.text ?? '',
                  origin: 'docx-native',
                },
                attributes: {},
              },
            });
          } catch {
            // Never block editing. A failure here is almost always the row
            // already existing; re-issuing it on every change is pointless.
          }
          knownRef.current.add(externalId);
        })
      );

      // 3. Reconcile: any pending row NOT in currentIds is now resolved/removed
      //    (accepted or rejected in-editor) → backend marks it `deleted`.
      await reconcile(documentId, currentIds);

      // The live doc's pending-change count == the number of distinct external
      // ids currently present (every one maps to a pending row after reconcile).
      return currentIds.length;
    },
    [documentId, documentType, createDocumentSuggestion, reconcile]
  );

  const sync = useCallback(
    (entries: readonly TrackedChangeEntry[] | null): Promise<number> => {
      const run = queueRef.current.then(() => runSync(entries));
      queueRef.current = run.catch(() => undefined);
      return run;
    },
    [runSync]
  );

  return useMemo(() => ({ sync }), [sync]);
}
